// Design System: Corporate Blue (Salesforce/SAP inspired)
// Navy sidebar, white content, strong contrast, professional tables

// Surface colors
export const surfaces = {
    base: "#f4f6f9",
    containerLow: "#eef1f6",
    container: "#e8ecf2",
    containerHigh: "#dde2ea",
    containerHighest: "#d2d8e2",
    containerLowest: "#ffffff",
    dim: "#c8cfd9",
};

// Corporate Blue palette
export const designTokens = {
    primary: {
        main: "#0b5cab",
        dim: "#094a8a",
        dark: "#1B2A4A",
        container: "#d6e8f7",
        onPrimary: "#ffffff",
        onContainer: "#0b5cab",
        fixed: "#d6e8f7",
        fixedDim: "#a8cce8",
    },
    secondary: {
        main: "#5a6a85",
        dim: "#47566d",
        container: "#e2e7ef",
        onSecondary: "#ffffff",
        onContainer: "#3d4a5c",
    },
    tertiary: {
        main: "#2e7d32",
        container: "#e8f5e9",
        onContainer: "#1b5e20",
    },
    error: {
        main: "#d32f2f",
        dim: "#b71c1c",
        container: "#ffebee",
        onError: "#ffffff",
        onContainer: "#b71c1c",
    },
    warning: {
        main: "#ed6c02",
        container: "#fff3e0",
    },
    surface: {
        main: "#f4f6f9",
        onSurface: "#1a2138",
        onSurfaceVariant: "#5a6a85",
        bright: "#ffffff",
    },
    outline: {
        main: "#c1c9d6",
        variant: "#dde2ea",
    },
    sidebar: {
        bg: "#1B2A4A",
        bgHover: "#243558",
        bgActive: "#0b5cab",
        text: "#a8b8d0",
        textActive: "#ffffff",
        textMuted: "#6b7fa0",
    },
};

// Legacy token mapping for backward compatibility
export const tokensDark = {
    grey: {
        0: "#ffffff",
        10: "#f4f6f9",
        50: "#eef1f6",
        100: "#e8ecf2",
        200: "#d2d8e2",
        300: "#c1c9d6",
        400: "#8896a8",
        500: "#5a6a85",
        600: "#47566d",
        700: "#1a2138",
        800: "#111827",
        900: "#0a0f1a",
        1000: "#000000",
    },
    primary: {
        100: "#d6e8f7",
        200: "#a8cce8",
        300: "#5a9fd4",
        400: "#0b5cab",
        500: "#094a8a",
        600: "#1B2A4A",
        700: "#152240",
        800: "#111827",
        900: "#0a0f1a",
    },
    secondary: {
        50: "#f4f6f9",
        100: "#e2e7ef",
        200: "#c1c9d6",
        300: "#8896a8",
        400: "#5a6a85",
        500: "#47566d",
        600: "#3d4a5c",
        700: "#2d3748",
        800: "#1a2138",
        900: "#111827",
    },
};

function reverseTokens(tokensDark) {
    const reversedTokens = {};
    Object.entries(tokensDark).forEach(([key, val]) => {
        const keys = Object.keys(val);
        const values = Object.values(val);
        const length = keys.length;
        const reversedObj = {};
        for (let i = 0; i < length; i++) {
            reversedObj[keys[i]] = values[length - i - 1];
        }
        reversedTokens[key] = reversedObj;
    });
    return reversedTokens;
}
export const tokensLight = reverseTokens(tokensDark);

// MUI theme
export const themeSettings = (mode) => {
    return {
        palette: {
            mode: mode,
            ...(mode === "dark"
                ? {
                      primary: {
                          ...tokensDark.primary,
                          main: designTokens.primary.main,
                          light: designTokens.primary.fixedDim,
                          dark: designTokens.primary.dim,
                      },
                      secondary: {
                          ...tokensDark.secondary,
                          main: designTokens.secondary.main,
                          light: "#8896a8",
                          dark: designTokens.secondary.dim,
                      },
                      neutral: {
                          ...tokensDark.grey,
                          main: tokensDark.grey[500],
                      },
                      background: {
                          default: "#111827",
                          alt: "#1a2138",
                          paper: "#1e2740",
                      },
                      error: { main: "#ef5350" },
                  }
                : {
                      primary: {
                          ...tokensLight.primary,
                          main: designTokens.primary.main,
                          light: designTokens.primary.fixedDim,
                          dark: designTokens.primary.dim,
                      },
                      secondary: {
                          ...tokensLight.secondary,
                          main: designTokens.secondary.main,
                          light: "#8896a8",
                          dark: designTokens.secondary.dim,
                      },
                      neutral: {
                          ...tokensLight.grey,
                          main: tokensDark.grey[500],
                      },
                      background: {
                          default: surfaces.base,
                          alt: surfaces.containerLowest,
                          paper: surfaces.containerLowest,
                      },
                      error: { main: designTokens.error.main },
                      success: { main: designTokens.tertiary.main },
                      warning: { main: designTokens.warning.main },
                  }),
        },
        typography: {
            fontFamily: ["Inter", "sans-serif"].join(","),
            fontSize: 13,
            h1: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 28,
                fontWeight: 700,
            },
            h2: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 22,
                fontWeight: 700,
            },
            h3: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 18,
                fontWeight: 600,
            },
            h4: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 16,
                fontWeight: 600,
            },
            h5: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 14,
                fontWeight: 600,
            },
            h6: {
                fontFamily: ["Inter", "sans-serif"].join(","),
                fontSize: 13,
                fontWeight: 600,
            },
        },
        shape: {
            borderRadius: 6,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 6,
                        padding: "7px 16px",
                        fontSize: "0.8125rem",
                        boxShadow: "none",
                        "&:hover": { boxShadow: "none" },
                    },
                    containedPrimary: {
                        backgroundColor: designTokens.primary.main,
                        color: "#ffffff",
                        "&:hover": {
                            backgroundColor: designTokens.primary.dim,
                        },
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 6,
                            "& fieldset": {
                                borderColor: designTokens.outline.main,
                            },
                            "&:hover fieldset": {
                                borderColor: designTokens.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: designTokens.primary.main,
                                borderWidth: 2,
                            },
                        },
                        "& .MuiInputLabel-root": {
                            fontSize: "0.875rem",
                            "&.Mui-focused": {
                                color: designTokens.primary.main,
                            },
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        border: `1px solid ${designTokens.outline.variant}`,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: "none",
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 4,
                        fontWeight: 600,
                        fontSize: "0.6875rem",
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        borderRight: "none",
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        "& .MuiTableCell-head": {
                            backgroundColor: "#eef1f6",
                            fontFamily: "Inter, sans-serif",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: designTokens.surface.onSurface,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            borderBottom: `2px solid ${designTokens.outline.main}`,
                            padding: "10px 16px",
                            whiteSpace: "nowrap",
                        },
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        "& .MuiTableRow-root": {
                            "&:hover": {
                                backgroundColor: "#f4f6f9",
                            },
                            "& .MuiTableCell-root": {
                                borderBottom: `1px solid ${designTokens.outline.variant}`,
                                padding: "10px 16px",
                                fontSize: "0.8125rem",
                                color: designTokens.surface.onSurface,
                            },
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 8,
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        borderRadius: 6,
                        border: `1px solid ${designTokens.outline.variant}`,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        borderRadius: 6,
                        border: `1px solid ${designTokens.outline.variant}`,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 4,
                        height: 6,
                        backgroundColor: designTokens.outline.variant,
                    },
                },
            },
        },
    };
};
